import debounce from "lodash-es/debounce"
import type { Coordinate } from "ol/coordinate"
import Feature, { type FeatureLike } from "ol/Feature"
import Circle from "ol/geom/Circle"
import Point from "ol/geom/Point"
import VectorLayer from "ol/layer/Vector"
import { bbox as bboxStrategy } from "ol/loadingstrategy"
import type OlMap from "ol/Map"
import { fromLonLat } from "ol/proj"
import VectorSource from "ol/source/Vector"
import { Fill, Stroke } from "ol/style"
import Style from "ol/style/Style"
import { storeToRefs } from "pinia"
import { useDisplay, useTheme } from "vuetify/framework"

const markerBadgeOffset = 3

export enum MarkerType {
  ASSET = "ASSET",
  POSITION = "POSITION",
}

const defaultMarkerSize = 1.6 as const

export const useRenderMarkers = (
  map: Ref<OlMap | null>,
  initialCenterOnLoad: Ref<boolean | undefined>,
  clusterZoomLevel: number,
  boundsByKey: Ref<BoundsByKey>,
  allAssets: Ref<Array<MapAsset>>,
  mainAsset: Ref<LookupAsset | undefined>,
  selectedFeatures: Ref<Array<FeatureLike>>,
  fitMapToVisibleMarkers: (fromClusters?: boolean) => void,
  refitAfterLoadOfCluster: Ref<boolean>,
  getClusterStyle: (feature: FeatureLike) => Style | Style[],
  trackPosition: Ref<boolean>,
) => {
  const theme = useTheme()
  const { xs, sm } = useDisplay()
  const $i18n = useI18n()
  const geoLocationWatch = ref<number | null>(null) // When tracking the user's location, this is the watch ID

  const baseMarkerSize = computed(() => {
    if (xs.value) {
      return defaultMarkerSize * 2
    }

    if (sm.value) {
      return defaultMarkerSize * 1.5
    }

    return defaultMarkerSize
  })

  const mainStore = useMainStore()
  const authStore = useAuthStore()
  const { initials } = storeToRefs(authStore)

  const { getLayer, removeLayer } = useLayerManagement(map)
  const { updateBounds, resetBounds } = useFeatureBounds(boundsByKey)

  const markerImagesByUrl = new Map<string, HTMLImageElement>()

  const reloadImages = debounce(() => {
    updateMarkers()
  }, 100)

  const getLoadedImageByUrl = (imageUrl: string): HTMLImageElement => {
    const existingImage = markerImagesByUrl.get(imageUrl)

    if (existingImage) {
      return existingImage
    }

    const newImage = new Image()
    newImage.src = imageUrl

    newImage.onload = reloadImages

    markerImagesByUrl.set(imageUrl, newImage)

    return newImage
  }

  const getMarkerScale = (size: MarkerSize) => {
    switch (size) {
      case MarkerSize.LARGE:
        return baseMarkerSize.value * 1.2
      case MarkerSize.MEDIUM:
        return baseMarkerSize.value
      case MarkerSize.SMALL:
        return baseMarkerSize.value * 0.8
    }
  }

  const updateMarkers = async () => {
    if (!map.value) return

    resetBounds(["markers"])
    removeLayer(OpenLayersLayer.MARKER)

    const assetMarkers = allAssets.value
      .filter((asset) => {
        if (!asset.location.gps?.coordinates || !asset.location.gps.coordinates[0] || !asset.location.gps.coordinates[1]) {
          return false
        }

        return true
      })
      .map((asset) => {
        const { color, size, description } = asset.category.marker

        const isSelected = selectedAssetIds.value.includes(asset._id)

        const src = svgAssetUrl(description, color, 1.5, isSelected ? parseColorToCSS(theme.current.value.colors.primary) : "#fff")

        if (!isSelected) {
          const selectedSrc = svgAssetUrl(description, color, 1.5, parseColorToCSS(theme.current.value.colors.primary))

          // Cache images so they don't flicker
          getLoadedImageByUrl(src)
          getLoadedImageByUrl(selectedSrc)
        }

        updateBounds("markers", asset.location.gps!.coordinates[1], asset.location.gps!.coordinates[0])

        return generateMarker(
          asset._id,
          `${asset.key} | ${asset.description}`,
          asset.location.gps!.coordinates as Coordinate,
          src,
          MarkerType.ASSET,
          size,
          // Check if geo asset, or lookup asset has any open tickets
          "supplierTicketStatistics" in asset
            ? // Geo asset
              !!asset.supplierTicketStatistics?.workTotal
            : // Lookup asset
              "ticketCount" in asset && asset.ticketCount
              ? !!asset.ticketCount?.action || !!asset.ticketCount?.inspection || !!asset.ticketCount?.malfunction
              : false,
          isSelected,
          "distance" in asset ? asset.distance : undefined,
        )
      })

    const features: Array<Feature<Point>> = [...assetMarkers]

    // Create vector source for markers
    const vectorSource = new VectorSource({
      features,
      strategy: bboxStrategy,
    })

    const zoomLayer: { minZoom?: number } = {}

    if (!mainAsset.value) {
      zoomLayer.minZoom = clusterZoomLevel
    }

    // This layer is used to cluster assets and tickets on top of each other
    const markerLayer = new VectorLayer({
      ...zoomLayer,
      source: vectorSource,
      style: getClusterStyle,
    })
    markerLayer.set("name", OpenLayersLayer.MARKER)

    // Add the vector layer to the map
    map.value.addLayer(markerLayer)

    if (
      !refitAfterLoadOfCluster.value &&
      ((allAssets.value?.length && refitAfterLoadOfCluster.value) || (!initialCenterOnLoad.value && allAssets.value?.length))
    ) {
      refitAfterLoadOfCluster.value = false
      initialCenterOnLoad.value = true
      fitMapToVisibleMarkers()
    }
  }

  const generateMarker = (
    id: string,
    name: string,
    coordinates: Coordinate,
    iconUrl: string,
    markerType: MarkerType,
    size = MarkerSize.MEDIUM,
    hasBadge = false,
    isSelected = false,
    distance?: number,
  ) => {
    const marker = new Feature({
      id,
      name,
      type: markerType,
      size: id === mainAsset.value?._id ? MarkerSize.LARGE : size,
      badge: hasBadge,
      geometry: new Point(fromLonLat(coordinates)),
      distance,
    })

    const markerIconStyle = new Style({
      renderer: (coordinates, { context, feature }) => {
        const [x = 0, y = 0] = coordinates as Coordinate

        const size = feature.get("size") as MarkerSize
        const hasBadge = feature.get("badge") as boolean

        const scale = 1.5 * getMarkerScale(size)

        const iconImage = getLoadedImageByUrl(iconUrl)

        const width = iconImage.width * scale
        const height = iconImage.height * scale

        context.drawImage(iconImage, x - width / 2, y - height, width, height)

        if (!hasBadge) return

        const badgeX = x + width / 2 - markerBadgeOffset
        const badgeY = y - height + markerBadgeOffset

        context.beginPath()
        context.arc(badgeX, badgeY, 6, 0, 2 * Math.PI)
        context.fillStyle = parseColorToCSS(theme.current.value.colors.error)

        context.lineWidth = 2
        context.strokeStyle = isSelected ? parseColorToCSS(theme.current.value.colors.primary) : "#fff"
        context.fill()
        context.stroke()
      },
    })

    marker.setStyle(markerIconStyle)

    return marker
  }

  const trackClientLocation = () => {
    if (!map.value) return

    if (!navigator.geolocation) {
      console.error("Geolocation is not supported by this browser.")
      return
    }

    const accuracyCircle = new Feature({
      id: "accuracy-circle",
    })

    const updatePositionMarker = (marker: Feature<Point>, position: GeolocationCoordinates) => {
      const { latitude, longitude, accuracy } = position

      console.debug(`Updating location: ${latitude}, ${longitude} (accuracy: ${accuracy}m)`)

      const transformedCoords = fromLonLat([longitude, latitude])

      const point = marker.getGeometry() as Point
      if (point) {
        point.setCoordinates(transformedCoords)
      } else {
        marker.setGeometry(new Point(transformedCoords))
      }

      marker.set("position", position.toJSON())

      // Update the accuracy circle
      accuracyCircle.set("name", $i18n.t("map.positionArea", Math.trunc(accuracy)))
      accuracyCircle.setGeometry(new Circle(transformedCoords, accuracy))
      accuracyCircle.setStyle(
        new Style({
          stroke: new Stroke({
            color: "rgba(0, 0, 255, 0.6)",
            width: 2,
          }),
          fill: new Fill({
            color: "rgba(0, 0, 255, 0.1)",
          }),
        }),
      )

      const positionLayer = getLayer(OpenLayersLayer.POSITION)
      if (!positionLayer) {
        // Vector layer for the user's location
        const layer = new VectorLayer({
          source: new VectorSource({
            features: [marker, accuracyCircle],
          }),
        })

        layer.set("name", OpenLayersLayer.POSITION)
        map.value!.addLayer(layer)
      }
    }

    const onLocationError = (error: GeolocationPositionError) => {
      mainStore.notify({
        type: "error",
        title: "Unable to track your location. Please check your browser settings.",
        text: error.message,
      })
    }

    const locationOptions: PositionOptions = {
      enableHighAccuracy: true, // Use high-accuracy mode for better tracking
      maximumAge: 0,
      timeout: Infinity,
    }

    navigator.geolocation.getCurrentPosition(
      (currentPosition) => {
        // Get current location so we can place a marker on the map
        const icon = svgAssetUrl(initials.value, parseColorToCSS(theme.current.value.colors.primary), 1.5, "#FFF")
        const marker = generateMarker(
          MarkerType.POSITION,
          initials.value,
          [currentPosition.coords.longitude, currentPosition.coords.latitude],
          icon,
          MarkerType.POSITION,
          MarkerSize.LARGE,
        )

        // Update position property and add circle style
        updatePositionMarker(marker, currentPosition.coords)

        // Update the location of the marker when the user moves
        geoLocationWatch.value = navigator.geolocation.watchPosition(
          (currentPosition) => updatePositionMarker(marker, currentPosition.coords),
          (error) => onLocationError(error),
          locationOptions,
        )
      },
      (error) => onLocationError(error),
      locationOptions,
    )
  }

  const stopTrackingClientLocation = () => {
    if (geoLocationWatch.value) {
      navigator.geolocation.clearWatch(geoLocationWatch.value)
      geoLocationWatch.value = null
    }

    removeLayer(OpenLayersLayer.POSITION)
  }

  const initializeMarkers = () => {
    updateMarkers()

    if (trackPosition.value) {
      trackClientLocation()
    }
  }

  const selectedAssetIds = computed(() => {
    return selectedFeatures.value
      .filter((feature) => feature.get("type") === MarkerType.ASSET)
      .map((feature) => feature.get("id") as string)
  })

  watch(selectedAssetIds, () => updateMarkers())

  watch(trackPosition, () => (trackPosition.value ? trackClientLocation() : stopTrackingClientLocation()))

  watch(allAssets, () => updateMarkers())

  onUnmounted(() => {
    stopTrackingClientLocation()
  })

  return {
    initializeMarkers,
  }
}
